import React, { useState } from 'react';
import {
  useStripe,
  useElements,
  PaymentElement,
  AddressElement
} from '@stripe/react-stripe-js';
import { 
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/solid';

interface PaymentFormProps {
  amount: number;
  lessonDetails: {
    teacherName: string;
    instrument: string;
    date: string;
    time: string;
    duration: number;
    lessonType: string;
  };
  onSuccess: (paymentIntent: any) => void;
  onError: (error: string) => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  lessonDetails,
  onSuccess,
  onError
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [processingStep, setProcessingStep] = useState<string>('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    console.log('Payment form submission started');
    
    if (!stripe || !elements) {
      console.error('Stripe not loaded');
      setErrorMessage('Payment system not ready. Please try again.');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setProcessingStep('Validating payment details...');

    try {
      // Simulate payment processing for demo
      console.log('Processing payment for lesson:', lessonDetails);
      console.log('Amount:', amount);
      
      // Step 1: Validate elements
      setProcessingStep('Validating payment information...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const { error: elementsError } = await elements.submit();
      if (elementsError) {
        throw new Error(elementsError.message);
      }

      // Step 2: Create payment intent (simulated)
      setProcessingStep('Securing payment...');
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In a real app, you'd call your backend to create a payment intent
      const mockPaymentIntent = {
        id: 'pi_' + Math.random().toString(36).substr(2, 9),
        amount: amount * 100, // Stripe uses cents
        currency: 'usd',
        status: 'succeeded',
        created: Date.now(),
        metadata: {
          teacherName: lessonDetails.teacherName,
          instrument: lessonDetails.instrument,
          lessonDate: lessonDetails.date,
          lessonTime: lessonDetails.time
        }
      };

      // Step 3: Confirm payment (simulated)
      setProcessingStep('Confirming payment...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Payment processed successfully:', mockPaymentIntent);
      
      // Step 4: Success
      setProcessingStep('Payment successful!');
      onSuccess(mockPaymentIntent);
      
    } catch (error: any) {
      console.error('Payment failed:', error);
      const message = error.message || 'An unexpected error occurred during payment processing.';
      setErrorMessage(message);
      onError(message);
    } finally {
      setIsLoading(false);
      setProcessingStep('');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <CreditCardIcon className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Payment Information</h3>
          <p className="text-sm text-gray-600">Secure payment processing by Stripe</p>
        </div>
      </div>

      {/* Lesson Summary */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-gray-900 mb-3">Lesson Summary</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-600">Teacher:</span>
            <span className="ml-2 font-medium text-gray-900">{lessonDetails.teacherName}</span>
          </div>
          <div>
            <span className="text-gray-600">Instrument:</span>
            <span className="ml-2 font-medium text-gray-900">{lessonDetails.instrument}</span>
          </div>
          <div>
            <span className="text-gray-600">Date & Time:</span>
            <span className="ml-2 font-medium text-gray-900">{lessonDetails.date}, {lessonDetails.time}</span>
          </div>
          <div>
            <span className="text-gray-600">Duration:</span>
            <span className="ml-2 font-medium text-gray-900">{lessonDetails.duration} minutes</span>
          </div>
          <div>
            <span className="text-gray-600">Type:</span>
            <span className="ml-2 font-medium text-gray-900">{lessonDetails.lessonType}</span>
          </div>
          <div className="col-span-2 pt-2 border-t border-gray-200">
            <span className="text-gray-600">Total Amount:</span>
            <span className="ml-2 text-lg font-bold text-gray-900">${amount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Payment Element */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Method
          </label>
          <div className="border border-gray-300 rounded-lg p-4">
            <PaymentElement 
              options={{
                layout: 'tabs'
              }}
            />
          </div>
        </div>

        {/* Billing Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Billing Address
          </label>
          <div className="border border-gray-300 rounded-lg p-4">
            <AddressElement 
              options={{
                mode: 'billing'
              }}
            />
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-red-800">Payment Error</h4>
              <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Processing Status */}
        {isLoading && processingStep && (
          <div className="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />
            <p className="text-sm text-blue-700">{processingStep}</p>
          </div>
        )}

        {/* Security Notice */}
        <div className="flex items-start space-x-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-600">
            <p className="font-medium">Secure Payment</p>
            <p>Your payment information is encrypted and secure. We never store your payment details.</p>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!stripe || isLoading}
          className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
            !stripe || isLoading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
              <span>Processing Payment...</span>
            </div>
          ) : (
            `Pay $${amount.toFixed(2)}`
          )}
        </button>

        {/* Payment Terms */}
        <p className="text-xs text-gray-500 text-center">
          By completing this payment, you agree to our Terms of Service and Privacy Policy. 
          Payment will be processed securely through Stripe.
        </p>
      </form>
    </div>
  );
};

export default PaymentForm; 